import Cookies from 'js-cookie';
import APIAuth from '@/api/modules/auth';
import axios from 'axios';

const isOnLoginPage = () => window.location.pathname.startsWith('/auth/');

const buildLoginUrl = () => {
  const { protocol, hostname, pathname, href } = window.location;
  const encodedData = pathname === '/' ? window.btoa(href + 'admin/doctors') : window.btoa(href);

  if (process.env.NODE_ENV === 'development') {
    return `http://localhost:8080/auth/login?redirectUrl=${encodedData}`;
  }

  return `${protocol}//${hostname}/auth/login?redirectUrl=${encodedData}`;
};

const goToLogin = () => {
  if (isOnLoginPage()) return;
  window.open(buildLoginUrl(), '_self');
};

// Auth-sv is OAuth2-shaped, so /auth/refresh may return the new token under
// `token`, `access_token`, or `accessToken` depending on whether crm-api
// normalizes it. Accept all three so a key mismatch can't silently log the
// user out on the first 401.
const extractToken = (data) =>
  data?.token ?? data?.access_token ?? data?.accessToken ?? null;

// Single-flight: when a page load fires many requests in parallel they all
// 401 at once after the access token expires. Without a shared promise each
// one would trigger its own /auth/refresh (refresh storm). We dedupe them so
// only one refresh runs and every waiter reuses its result.
let refreshPromise = null;

const performRefresh = () =>
  APIAuth.refreshToken().then((authResponse) => {
    const newToken = extractToken(authResponse?.data);
    if (!newToken) {
      throw new Error('Refresh response did not contain a token');
    }

    const domain = window.location.hostname.split('.').slice(-2).join('.');
    Cookies.set('adminToken', newToken, { path: '/', domain, expires: 1 / 3 });

    return newToken;
  });

const microserverConfig = (instance) => {
  instance.interceptors.request.use((config) => {
    if (!Cookies.get('adminToken')) {
      goToLogin();
      return Promise.reject(new Error('No admin token; request aborted'));
    }

    return config;
  });

  instance.interceptors.response.use(undefined, (error) => {
    const response = error?.response;

    if (response && response.status === 401) {
      const originalRequest = response.config;

      if (!refreshPromise) {
        refreshPromise = performRefresh().finally(() => {
          refreshPromise = null;
        });
      }

      return refreshPromise.then((newToken) => {
        originalRequest.headers['Authorization'] = newToken;
        // Retry via bare axios (no interceptor) so a repeated 401 can't loop.
        return axios(originalRequest);
      }).catch((refreshError) => {
        goToLogin();
        return Promise.reject(refreshError);
      });
    }

    return Promise.reject(error);
  });

  return instance;
};

export default microserverConfig;
